import { enqueueSnackbar } from '@common-modules/snackbar';
import {
  actionApplyDeleteTranscription,
  DELETE_TRANSCRIPTION_RESULTS,
  DELETE_TRANSCRIPTION_RESULTS_FAILURE,
  DELETE_TRANSCRIPTION_RESULTS_SUCCESS,
} from '../actions/transcription';
import { deleteTranscriptionResults } from '../services';
import { sagaIntl } from '@i18n';
import { IN_APPLY_DELETE_TRANSCRIPTION } from '@common/web-worker';
import { race, select, take, takeLatest, put } from 'typed-redux-saga/macro';
import { processEngineRequestAction } from '@common-modules/engines/transcription';
import { processEngineRequestAction as separateAgainAction } from '@common-modules/engines/speaker-separation';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import { mediaStreamerDownloadUrl } from '@helpers/engineJobHelper';
import { selectTdo } from '../selectors';

export function* watchDeleteTranscriptionResults() {
  yield* takeLatest(
    DELETE_TRANSCRIPTION_RESULTS,
    handleDeleteTranscriptionResults
  );
}

export function* handleDeleteTranscriptionResults({
  payload: {
    removeAudioRedactions,
    removeVoiceMask,
    thenTranscribeIn,
    thenSeparate,
  },
}: ReturnType<typeof DELETE_TRANSCRIPTION_RESULTS>) {
  /**
   * The media this delete is for, read BEFORE it starts.
   *
   * The delete is a paged asset scan plus a mutation per asset — seconds, and
   * the user is free to open another file while it runs. `deleteTranscription
   * Results` snapshots the TDO on entry and stays correctly scoped; the
   * replacement run used to re-read it afterwards and so followed the screen,
   * starting a transcription (and now a separation) against whatever media had
   * been opened in the meantime, while the file the user actually asked about
   * had its transcript deleted and got nothing back. Both halves silent.
   *
   * Reading it once, here, is what ties the whole operation to one TDO.
   */
  const target = yield* select(selectTdo);

  yield* put(deleteTranscriptionResults());

  const { success } = yield* race({
    success: take(DELETE_TRANSCRIPTION_RESULTS_SUCCESS),
    failure: take(DELETE_TRANSCRIPTION_RESULTS_FAILURE),
  });

  const intl = sagaIntl();

  if (success) {
    const metadata = {
      description: intl.formatMessage({
        id: 'deleteTranscriptionHistory',
        defaultMessage: 'delete transcription',
      }),
    };

    yield* put(
      actionApplyDeleteTranscription({ removeAudioRedactions, removeVoiceMask })
    );
    yield* put(
      IN_APPLY_DELETE_TRANSCRIPTION({
        removeAudioRedactions,
        removeVoiceMask,
        metadata,
      })
    );
    if (thenTranscribeIn) {
      yield* transcribeAgain(target, thenTranscribeIn, thenSeparate);
      return;
    }

    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'deleteTranscriptionSuccess',
          defaultMessage: 'Transcription deleted successfully',
        }),
      })
    );
    return;
  }

  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'deleteTranscriptionFailure',
        defaultMessage: 'Failed to delete transcription',
      }),
    })
  );
}

/**
 * Start the replacement run, now that the old transcript is gone.
 *
 * Takes the TDO rather than selecting one: by the time this runs the caller's
 * dialog has closed and the user may be looking at different media, and the
 * run belongs to the file whose transcript was just deleted — see where
 * `target` is read. apiRoot is still selected here because it is a property of
 * the environment, not of the media. Same media-streamer URL the direct run
 * path uses (VE-30662) — never the asset's signed URI, which the payload of a
 * delete is a poor place to carry.
 *
 * Silent when there is no TDO to run against: the delete itself succeeded, and
 * a second error message about an impossible follow-up would only confuse what
 * just happened.
 */
function* transcribeAgain(
  tdo: ReturnType<typeof selectTdo>,
  transcriptionLanguage: string,
  separate?: boolean
) {
  const { apiRoot } = yield* select(selectConfigEngines);

  if (!tdo?.id) {
    return;
  }

  const url = mediaStreamerDownloadUrl(apiRoot, String(tdo.id));

  yield* put(
    processEngineRequestAction({
      tdoId: tdo.id,
      tdoName: tdo.name,
      targetId: String(tdo.id),
      url,
      transcriptionLanguage,
    })
  );

  // Its own job, started alongside the transcription rather than after it —
  // the same shape the Run Engines dialog uses on a first run, and for the
  // same reason: Speechmatics diarizes the audio itself, and the Veritone
  // engine waits for the transcript on its own. The language has to be SENT:
  // this job would otherwise read it off the transcript on screen, which this
  // delete just removed.
  if (separate) {
    yield* put(
      separateAgainAction({
        tdoId: tdo.id,
        tdoName: tdo.name,
        targetId: String(tdo.id),
        url,
        transcriptionLanguage,
      })
    );
  }
}
